/**
 * API client + query hooks for every back-office endpoint.
 *
 * Each list endpoint follows the same shape — { content, page, size,
 * totalElements, totalPages } — and is wrapped in an ApiResponse<T>
 * envelope `{ status, message, data }` by the Spring server.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  CustomerRequest,
  Customer,
  Payment,
  NotificationLogEntry,
  RequestDocument,
} from '@/types/request';

export interface Page<T> {
  content: T[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

interface ApiEnvelope<T> {
  status: string;
  message?: string;
  data: T;
}

// ─── Customer requests ─────────────────────────────────────────────────────

export interface RequestsListParams {
  search?: string;
  status?: string[];
  type?: string[];
  fixedType?: string | null;
  page?: number;
  size?: number;
}

export function useRequests(params: RequestsListParams) {
  const types = [
    ...(params.type ?? []),
    ...(params.fixedType ? [params.fixedType] : []),
  ];
  return useQuery<Page<CustomerRequest>>({
    queryKey: ['requests', params],
    queryFn: async () => {
      const r = await api.get<ApiEnvelope<Page<unknown>>>('/back-office/customer-requests', {
        params: {
          search: params.search?.trim() || undefined,
          status: params.status?.length ? params.status : undefined,
          type: types.length ? types : undefined,
          page: params.page ?? 0,
          size: params.size ?? 25,
        },
        paramsSerializer: { indexes: null },   // ?status=A&status=B (not [0]=A)
      });
      return mapRequestPage(r.data.data);
    },
  });
}

export function useRequestDetail(id: string | null) {
  return useQuery<CustomerRequest>({
    enabled: !!id,
    queryKey: ['request', id],
    queryFn: async () => {
      const r = await api.get<ApiEnvelope<unknown>>(`/back-office/customer-requests/${id}`);
      return mapRequest(r.data.data);
    },
  });
}

export function useUpdateRequestStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; status: string; note?: string }) => {
      const r = await api.put<ApiEnvelope<unknown>>(
        `/back-office/customer-requests/${vars.id}/status`,
        { status: vars.status, note: vars.note },
      );
      return mapRequest(r.data.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['requests'] });
      qc.invalidateQueries({ queryKey: ['request'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

// ─── Customers ─────────────────────────────────────────────────────────────

export interface CustomersListParams {
  search?: string;
  status?: string;
  serviceType?: string;
  page?: number;
  size?: number;
}

export function useCustomers(params: CustomersListParams) {
  return useQuery<Page<Customer>>({
    queryKey: ['customers', params],
    queryFn: async () => {
      const r = await api.get<ApiEnvelope<Page<unknown>>>('/back-office/customers', {
        params: {
          search: params.search?.trim() || undefined,
          status: params.status || undefined,
          serviceType: params.serviceType || undefined,
          page: params.page ?? 0,
          size: params.size ?? 25,
        },
      });
      return mapCustomerPage(r.data.data);
    },
  });
}

export function useCustomerDetail(accountId: string | null) {
  return useQuery({
    enabled: !!accountId,
    queryKey: ['customer', accountId],
    queryFn: async () => {
      const r = await api.get<ApiEnvelope<RawCustomerDetail>>(`/back-office/customers/${accountId}`);
      return r.data.data;
    },
  });
}

// ─── Payments ──────────────────────────────────────────────────────────────

export interface PaymentsListParams {
  search?: string;
  status?: string;
  page?: number;
  size?: number;
}

export function usePayments(params: PaymentsListParams) {
  return useQuery<Page<Payment>>({
    queryKey: ['payments', params],
    queryFn: async () => {
      const r = await api.get<ApiEnvelope<Page<unknown>>>('/back-office/payments', {
        params: {
          search: params.search?.trim() || undefined,
          status: params.status || undefined,
          page: params.page ?? 0,
          size: params.size ?? 25,
        },
      });
      return mapPaymentPage(r.data.data);
    },
  });
}

// ─── Notifications log ─────────────────────────────────────────────────────

export interface NotificationLogParams {
  search?: string;
  channel?: NotificationLogEntry['channel'];
  status?: NotificationLogEntry['status'];
  page?: number;
  size?: number;
}

interface RawNotificationLogEntry {
  id: string;
  sentAt?: string;
  recipient?: string;
  channel?: string;
  title?: string;
  bodyPreview?: string;
  status?: string;
}

export function useNotificationLog(params: NotificationLogParams) {
  return useQuery<Page<NotificationLogEntry>>({
    queryKey: ['notification-log', params],
    queryFn: async () => {
      const r = await api.get<ApiEnvelope<Page<RawNotificationLogEntry>>>(
        '/back-office/notifications/log',
        {
          params: {
            search: params.search?.trim() || undefined,
            channel: params.channel || undefined,
            status: params.status || undefined,
            page: params.page ?? 0,
            size: params.size ?? 20,
          },
        },
      );
      return mapNotificationLogPage(r.data.data);
    },
  });
}

function mapNotificationLogEntry(row: RawNotificationLogEntry): NotificationLogEntry {
  const channel = (row.channel ?? 'SMS').toUpperCase();
  const status = (row.status ?? 'PENDING').toUpperCase();
  return {
    id: String(row.id),
    sentAt: row.sentAt ?? new Date().toISOString(),
    recipient: row.recipient ?? '—',
    channel: (channel === 'EMAIL' || channel === 'PUSH' ? channel : 'SMS') as NotificationLogEntry['channel'],
    title: row.title ?? 'Notification',
    bodyPreview: row.bodyPreview ?? '',
    status: (status === 'DELIVERED' || status === 'FAILED' ? status : 'PENDING') as NotificationLogEntry['status'],
  };
}

function mapNotificationLogPage(
  p: Page<RawNotificationLogEntry>,
): Page<NotificationLogEntry> {
  return { ...p, content: (p.content ?? []).map(mapNotificationLogEntry) };
}

// ─── Chat ──────────────────────────────────────────────────────────────────

export interface ChatConversation {
  id: string;
  userId: string;
  accountNumber?: string;
  requestId?: string;
  requestReference?: string;
  customerName?: string;
  customerPhone?: string;
  lastMessagePreview?: string;
  lastMessageAt?: string;
  unreadForCustomer: number;
  unreadForStaff: number;
  createdAt: string;
  updatedAt: string;
}

export interface ChatMessage {
  id: string;
  conversationId: string;
  authorId?: string;
  authorName: string;
  authorKind: 'CUSTOMER' | 'STAFF' | 'SYSTEM';
  body: string;
  internal: boolean;
  createdAt: string;
}

/**
 * Find-or-create a conversation pinned to a given request. The backend POST
 * is idempotent — calling it with the same userId returns the existing
 * conversation if one already exists, otherwise creates and returns a new
 * one with the request reference attached.
 *
 * We use useQuery (not useMutation) because the result is conceptually a
 * read: open the drawer → fetch the conversation → render. The mutation
 * fires once per request id and is cached.
 */
export interface ConversationStartParams {
  requestId: string;
  userId?: string;
  customerName?: string;
  customerPhone?: string;
  accountNumber?: string | null;
  requestReference?: string;
}

export function useConversationForRequest(params: ConversationStartParams | null) {
  return useQuery<ChatConversation>({
    enabled: !!params && !!params.userId,
    queryKey: ['conversation-for-request', params?.requestId],
    queryFn: async () => {
      const r = await api.post<ApiEnvelope<ChatConversation>>(
        '/back-office/chat/conversations',
        {
          requestId: params!.requestId,
          userId: params!.userId,
          customerName: params!.customerName,
          customerPhone: params!.customerPhone,
          accountNumber: params!.accountNumber ?? undefined,
          requestReference: params!.requestReference,
        },
      );
      return r.data.data;
    },
    staleTime: 60_000,
  });
}

export function useConversations(search: string) {
  return useQuery<Page<ChatConversation>>({
    queryKey: ['conversations', search],
    queryFn: async () => {
      const r = await api.get<ApiEnvelope<Page<ChatConversation>>>(
        '/back-office/chat/conversations',
        { params: { search: search.trim() || undefined, page: 0, size: 50 } },
      );
      return r.data.data;
    },
    refetchInterval: 15_000,
  });
}

export function useConversationMessages(id: string | null) {
  return useQuery<ChatMessage[]>({
    enabled: !!id,
    queryKey: ['messages', id],
    queryFn: async () => {
      const r = await api.get<ApiEnvelope<ChatMessage[]>>(
        `/back-office/chat/conversations/${id}/messages`,
      );
      return r.data.data;
    },
    refetchInterval: 5_000,
  });
}

export function useSendMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { conversationId: string; body: string; internal: boolean }) => {
      const r = await api.post<ApiEnvelope<ChatMessage>>(
        `/back-office/chat/conversations/${vars.conversationId}/messages`,
        { body: vars.body, internal: vars.internal },
      );
      return r.data.data;
    },
    onSuccess: (_m, vars) => {
      qc.invalidateQueries({ queryKey: ['messages', vars.conversationId] });
      qc.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

// ─── Dashboard ─────────────────────────────────────────────────────────────

export interface DashboardSummary {
  pendingReview: number;
  approvedThisWeek: number;
  rejectedThisWeek: number;
  countsByType: { type: string; count: number }[];
  recent: CustomerRequest[];
}

export function useDashboard() {
  return useQuery<DashboardSummary>({
    queryKey: ['dashboard'],
    queryFn: async () => {
      const r = await api.get<ApiEnvelope<RawDashboard>>('/back-office/dashboard/summary');
      const d = r.data.data;
      return {
        pendingReview: d.pendingReview,
        approvedThisWeek: d.approvedThisWeek,
        rejectedThisWeek: d.rejectedThisWeek,
        countsByType: d.countsByType ?? [],
        recent: (d.recent ?? []).map(mapRequest),
      };
    },
  });
}

// ─── Mappers ──────────────────────────────────────────────────────────────
// Translate the backend DTOs into the shape the existing components already
// know. Keeps the components untouched as much as possible.

function mapRequest(row: unknown): CustomerRequest {
  const r = row as Record<string, unknown>;
  const num = (v: unknown): number | undefined => {
    if (v == null) return undefined;
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : undefined;
  };
  return {
    id: String(r.id),
    referenceNumber: String(r.referenceNumber ?? ''),
    requestType: String(r.requestType ?? 'JISOMEE_METER') as CustomerRequest['requestType'],
    status: String(r.status ?? 'SUBMITTED') as CustomerRequest['status'],
    userId: (r.userId as string) ?? undefined,
    accountNumber: (r.accountNumber as string) ?? null,
    accountName: (r.accountName as string) ?? null,
    contactName: String(r.contactName ?? '—'),
    contactMobile: String(r.contactMobile ?? '—'),
    contactEmail: (r.contactEmail as string) ?? undefined,
    location: (r.location as string) ?? null,
    latitude: num(r.latitude),
    longitude: num(r.longitude),
    totalAmount: r.totalAmount == null ? null : Number(r.totalAmount),
    quantityValue: num(r.quantityValue),
    quantityUnit: (r.quantityUnit as string) ?? undefined,
    reason: (r.reason as string) ?? undefined,
    description: (r.comment as string) ?? undefined,
    readingMeterNumber: (r.readingMeterNumber as string) ?? undefined,
    readingPrevious: num(r.readingPrevious),
    readingCurrent: num(r.readingCurrent),
    readingUnits: num(r.readingUnits),
    readingType: (r.readingType as string) ?? undefined,
    submittedAt: (r.submittedAt as string) ?? new Date().toISOString(),
    updatedAt: (r.updatedAt as string) ?? new Date().toISOString(),
    photoUrls: r.readingPhotoBase64
      ? [`data:image/jpeg;base64,${r.readingPhotoBase64 as string}`]
      : undefined,
    documents: extractDocuments(r),
    notes: [],
    activity: [],
  };
}

/**
 * Pulls each idDocument/kraDocument/applicationForm/etc base64 + filename
 * pair off the raw row and packages them into a RequestDocument[]. Skips
 * pairs where either side is missing.
 */
function extractDocuments(r: Record<string, unknown>): RequestDocument[] {
  const SOURCES: { kind: RequestDocument['kind']; label: string; base: string }[] = [
    { kind: 'ID',                 label: 'ID document',         base: 'idDocument' },
    { kind: 'KRA_PIN',            label: 'KRA PIN',             base: 'kraDocument' },
    { kind: 'ALT_ID',             label: 'Alternative ID',      base: 'altIdDocument' },
    { kind: 'APPLICATION_FORM',   label: 'Application form',    base: 'applicationForm' },
    { kind: 'WATER_BILL',         label: 'Water bill',          base: 'waterBill' },
    { kind: 'TENANCY_AGREEMENT',  label: 'Tenancy agreement',   base: 'tenancyAgreement' },
  ];
  const out: RequestDocument[] = [];
  for (const s of SOURCES) {
    const filename = r[`${s.base}Filename`] as string | undefined;
    const base64 = r[`${s.base}Base64`] as string | undefined;
    if (!filename || !base64) continue;
    const mime = mimeFromFilename(filename);
    out.push({
      kind: s.kind,
      label: s.label,
      filename,
      dataUrl: `data:${mime};base64,${base64}`,
      isImage: mime.startsWith('image/'),
    });
  }
  return out;
}

function mimeFromFilename(filename: string): string {
  const ext = filename.toLowerCase().split('.').pop();
  switch (ext) {
    case 'pdf':  return 'application/pdf';
    case 'jpg':
    case 'jpeg': return 'image/jpeg';
    case 'png':  return 'image/png';
    case 'gif':  return 'image/gif';
    case 'webp': return 'image/webp';
    case 'heic': return 'image/heic';
    default:     return 'application/octet-stream';
  }
}

function mapRequestPage(p: Page<unknown>): Page<CustomerRequest> {
  return { ...p, content: (p.content ?? []).map(mapRequest) };
}

interface RawCustomer {
  accountId: number | string;
  accountNumber?: string;
  accountName?: string;
  mobileNumber?: string;
  email?: string;
  balance?: number;
  accountStatus?: string;
  serviceType?: string;
  location?: string;
}

interface RawCustomerDetail extends RawCustomer {
  customerId?: number;
  legacyAccountNumber?: string;
  landlordName?: string;
  meterNumber?: string;
  meterSize?: number;
  meterType?: string;
  averageConsumption?: number;
  agingDays?: number;
  agingColor?: string;
  lastReadingDate?: string;
  deposit?: number;
  estimatedBalance?: number;
  pipelineCode?: string;
  connectionNumber?: string;
  serviceType?: string;
}

function mapCustomer(row: unknown): Customer {
  const c = row as RawCustomer;
  const statusLabel = (c.accountStatus ?? '').trim();
  return {
    id: String(c.accountId),
    accountNumber: c.accountNumber ?? '—',
    name: c.accountName ?? '—',
    phone: c.mobileNumber ?? '—',
    email: c.email,
    balance: c.balance ?? 0,
    statusLabel: statusLabel || '—',
    verifiedAccountsCount: 1,
    totalPaidKes: 0,
    lastActiveAt: new Date().toISOString(),
    status: statusLabel.toUpperCase().includes('ACTIVE') ? 'ACTIVE' : 'SUSPENDED',
    accounts: c.accountNumber
      ? [{
          id: String(c.accountId),
          accountNumber: c.accountNumber,
          balance: c.balance ?? 0,
          location: c.location ?? '',
        }]
      : [],
  };
}

function mapCustomerPage(p: Page<unknown>): Page<Customer> {
  return { ...p, content: (p.content ?? []).map(mapCustomer) };
}

interface RawPayment {
  id: number | string;
  transactionId?: string;
  transactionTime?: string;
  amount?: number;
  accountNumber?: string;
  customerName?: string;
  msisdn?: string;
  status?: string;
  c2bStatus?: string;
}

function mapPayment(row: unknown): Payment {
  const p = row as RawPayment;
  const status = (p.status ?? p.c2bStatus ?? '').toUpperCase();
  const mappedStatus: Payment['status'] =
    status.includes('SUCCESS') || status.includes('COMPLETED')
      ? 'SUCCESS'
      : status.includes('FAIL')
        ? 'FAILED'
        : status.includes('CANCEL') || status.includes('REVERSE')
          ? 'CANCELLED'
          : 'PENDING';
  return {
    id: String(p.id),
    referenceNumber: p.transactionId ?? `#${p.id}`,
    customer: { id: '', name: p.customerName ?? '—' },
    accountNumber: p.accountNumber ?? '',
    method: 'MPESA',
    amount: p.amount ?? 0,
    status: mappedStatus,
    initiatedAt: p.transactionTime
      ? parseLegacyDate(p.transactionTime)
      : new Date().toISOString(),
  };
}

function mapPaymentPage(p: Page<unknown>): Page<Payment> {
  return { ...p, content: (p.content ?? []).map(mapPayment) };
}

interface RawDashboard {
  pendingReview: number;
  approvedThisWeek: number;
  rejectedThisWeek: number;
  countsByType?: { type: string; count: number }[];
  recent?: unknown[];
}

/**
 * Legacy stores transaction time as a string in "yyyyMMddHHmmss" form (the
 * M-Pesa C2B convention). Convert to ISO so date-fns can render it.
 */
function parseLegacyDate(raw: string): string {
  if (/^\d{14}$/.test(raw)) {
    const y = raw.slice(0, 4);
    const mo = raw.slice(4, 6);
    const d = raw.slice(6, 8);
    const h = raw.slice(8, 10);
    const mi = raw.slice(10, 12);
    const s = raw.slice(12, 14);
    return `${y}-${mo}-${d}T${h}:${mi}:${s}Z`;
  }
  // Try ISO already
  const d = new Date(raw);
  return isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
}
